import { type ReactElement, Suspense, useCallback, useEffect, useState } from "react";

import { APP_V3_APP_EVENTS, logEvent } from "../../lib/analytics";
import { RememberEmailDialog } from "./RememberEmailDialog";
import {
  clearPendingRememberEmailPrompt,
  forgetRememberedEmail,
  getPendingRememberEmailPrompt,
  REMEMBER_EMAIL_PROMPT_EVENT,
  rememberEmail,
  suppressRememberEmailPrompt,
} from "./rememberEmailStore";

/**
 * Shows the post-sign-in "remember your email?" prompt (DEVOP-5493).
 *
 * Mounted once at the app root so it survives the post-sign-in redirect: the
 * sign-in flows call `requestRememberEmail`, which records the email and fires
 * `REMEMBER_EMAIL_PROMPT_EVENT`; this manager picks it up and shows the dialog
 * after the redirect. Because it never blocks the redirect, a slow or
 * unanswered prompt can't time out the sign-in or get torn down with the auth
 * page (the problems a per-page blocking dialog had).
 */
export function RememberEmailPromptManager(): ReactElement | null {
  const [email, setEmail] = useState<string | undefined>(undefined);

  useEffect(() => {
    function showPendingPrompt(): void {
      const pending = getPendingRememberEmailPrompt();
      if (pending) {
        setEmail(pending);
      }
    }

    // Catch a prompt requested before this manager mounted, then listen for
    // subsequent requests for the lifetime of the app.
    showPendingPrompt();
    globalThis.addEventListener(REMEMBER_EMAIL_PROMPT_EVENT, showPendingPrompt);
    return () => {
      globalThis.removeEventListener(REMEMBER_EMAIL_PROMPT_EVENT, showPendingPrompt);
    };
  }, []);

  const close = useCallback(() => {
    setEmail(undefined);
    clearPendingRememberEmailPrompt();
  }, []);

  const handleRemember = useCallback(() => {
    setEmail((current) => {
      if (current) {
        rememberEmail(current);
        logEvent(APP_V3_APP_EVENTS.REMEMBER_EMAIL_PROMPT_ACCEPTED);
      }
      return undefined;
    });
    clearPendingRememberEmailPrompt();
  }, []);

  const handleDecline = useCallback(() => {
    forgetRememberedEmail();
    logEvent(APP_V3_APP_EVENTS.REMEMBER_EMAIL_PROMPT_DECLINED);
    close();
  }, [close]);

  const handleSuppress = useCallback(() => {
    // Stores the "don't ask again" sentinel in `emailForSignIn`, which also
    // clears any remembered address.
    suppressRememberEmailPrompt();
    logEvent(APP_V3_APP_EVENTS.REMEMBER_EMAIL_PROMPT_SUPPRESSED);
    close();
  }, [close]);

  // Only mount the (design-system) dialog when a prompt is actually pending, so
  // the common no-prompt case adds nothing to the app-root render. Isolate it in
  // a Suspense boundary so the prompt can never stall the whole app.
  if (email === undefined) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <RememberEmailDialog
        open
        email={email}
        onRemember={handleRemember}
        onDecline={handleDecline}
        onSuppress={handleSuppress}
      />
    </Suspense>
  );
}
